// The respondent side of the console — PMO Self-Assessment MVP
// (PRODUCT_OWNER_DECISION_PMO_SELF_ASSESSMENT_MVP 240926.md §6;
// DESIGN_PMO_SELF_ASSESSMENT_MVP 240926 V1.0.md §4.2–§4.4).
//
// PURE. No fetch, no supabase, no React: everything here is a decision over
// data the screens have already loaded, so each rule is testable on its own
// and none of them can be quietly re-derived differently in two places.
//
// THE BOUNDARY THIS DRAWS IS PRESENTATION, NOT SECURITY. Deciding that a user
// sees the respondent view rather than the consultant workspace is the
// decision's "the existing LINNFOSS consultant workspace must not be exposed
// to the respondent" — a usability control. The data boundary is the
// database's: RLS tenancy, `assert_run_editable` (0008) for who may answer,
// and 0019's lock for who may reopen. Nothing below is trusted to enforce
// any of those, and nothing below weakens them if it is wrong.

import type { MembershipRole } from "./types";

/** A run assigned to the signed-in user, with the one fact that makes it
 *  self-serve read from its pinned, frozen definition. */
export interface AssignedRun {
  run_id: string;
  organisation_id: string;
  state: string;
  instrument_version_id: string;
  self_serve: boolean;
}

export type ViewerMode = "respondent" | "consultant";

/**
 * Which console a signed-in user gets.
 *
 * A RESPONDENT is someone who has been assigned at least one self-serve run
 * and holds no role that administers or leads assessment anywhere. Both
 * halves matter:
 *
 *  - the LINNFOSS administrator is `owner` of the customer's organisation
 *    (request A1), so a respondent test on "has a self-serve assignment"
 *    alone would lock the administrator out of the workspace the moment
 *    they assigned a run to themselves while testing;
 *  - a consultant who is an `assessor` somewhere but has no self-serve
 *    assignment keeps the workspace they have today — PAQ unaffected.
 *
 * Everyone else, including a user with no memberships at all, is shown the
 * consultant console exactly as before, which already renders its own
 * "you are not a member of any organisation" state.
 */
export function viewerMode(
  roles: readonly MembershipRole[],
  assigned: readonly AssignedRun[],
  selfAssessmentMember = false,
): ViewerMode {
  const leads = roles.some((r) => r === "owner" || r === "lead_assessor");
  if (leads) return "consultant";
  // D6: a non-lead member of a self-assessment tenant is a respondent even
  // before (or after) any assignment — the workspace would otherwise show
  // them its member lists, which 0019 §6 empties at the database anyway.
  if (selfAssessmentMember) return "respondent";
  return assigned.some((a) => a.self_serve) ? "respondent" : "consultant";
}

/**
 * Decision D6 (0019 §6): is the user a member of an organisation marked
 * `self_assessment`, in a role that neither owns nor leads there? The mark
 * is read from `organisations`; before 0019 is applied the column is absent,
 * which reads as "not marked", so the console runs against either schema.
 */
export function isSelfAssessmentMember(
  memberships: readonly { organisation_id: string; role: MembershipRole }[],
  organisations: readonly { id: string; self_assessment?: boolean | null }[],
): boolean {
  const marked = new Set(organisations.filter((o) => o.self_assessment === true).map((o) => o.id));
  return memberships.some(
    (m) => marked.has(m.organisation_id) && m.role !== "owner" && m.role !== "lead_assessor",
  );
}

/** States in which a respondent is still answering (0008's editable set). */
const ANSWERING = new Set(["draft", "ready_for_review"]);

/**
 * The one assessment a respondent opens. The MVP is one respondent, one
 * assessment (decision §6); if a second ever appears, the one still being
 * answered wins over a submitted one, and among equals the choice is
 * stable (by run id), never "whatever the query returned first".
 */
export function currentAssessment(assigned: readonly AssignedRun[]): AssignedRun | null {
  const own = assigned.filter((a) => a.self_serve);
  if (own.length === 0) return null;
  const sorted = [...own].sort((a, b) => {
    const ao = ANSWERING.has(a.state) ? 0 : 1;
    const bo = ANSWERING.has(b.state) ? 0 : 1;
    return ao - bo || a.run_id.localeCompare(b.run_id);
  });
  return sorted[0] ?? null;
}

export function isAnswering(state: string): boolean {
  return ANSWERING.has(state);
}

export function isSubmitted(state: string): boolean {
  return state === "finalized" || state === "published";
}

// ---- questionnaire progress ------------------------------------------------

export interface QuestionRef {
  block_key: string;      // the area's block id in the definition
  criterion_key: string;  // the question's criterion id
}

export interface AnswerRef {
  block_key: string;
  criterion_key: string;
  applicability: string;
  score: number | null;
  rationale?: string | null;
}

/** An area's own response: the whole area declared not applicable, or not. */
export interface AreaRef {
  block_key: string;
  applicability: string;
  rationale?: string | null;
}

export interface AreaProgress {
  block_key: string;
  answered: number;       // questions with a DELIBERATE response
  total: number;
  complete: boolean;
  notApplicable: boolean; // the whole area is declared not applicable, with a reason
}

export interface Progress {
  answered: number;
  total: number;
  areas: AreaProgress[];
  canSubmit: boolean;
}

const hasText = (t: string | null | undefined) => (t ?? "").trim().length > 0;

/**
 * Whether one response is DELIBERATE — the Business Architect's C3 line
 * between "unanswered" and "Unable to assess", which 0019 section 3 also
 * enforces at submission:
 *
 *   - an answer on the scale (`assessed` with a score);
 *   - "Unable to assess" (`not_assessed`, a row that exists);
 *   - "Not applicable" WITH a justification — the kernel refuses an N/A
 *     without one (`na_without_rationale`), so an unjustified N/A would pass
 *     here only to fail the evaluation.
 *
 * A row marked `assessed` with no score is a half-finished answer, and no
 * row at all is a question not yet reached. Neither counts.
 */
export function isDeliberate(a: AnswerRef): boolean {
  if (a.applicability === "assessed") return a.score !== null;
  if (a.applicability === "not_assessed") return true;
  if (a.applicability === "not_applicable") return hasText(a.rationale);
  return false;
}

/**
 * What has been answered, per area and overall. An area declared not
 * applicable WITH a justification accounts for all of its questions; without
 * one, for none. `canSubmit` requires every question accounted for — the
 * same rule 0019 applies when the run is submitted, so the button and the
 * database cannot disagree.
 */
export function progress(
  questions: readonly QuestionRef[],
  answers: readonly AnswerRef[],
  areaResponses: readonly AreaRef[] = [],
): Progress {
  const done = new Set(
    answers.filter(isDeliberate).map((a) => `${a.block_key}\u0000${a.criterion_key}`),
  );
  const areaNA = new Set(
    areaResponses
      .filter((r) => r.applicability === "not_applicable" && hasText(r.rationale))
      .map((r) => r.block_key),
  );
  const order: string[] = [];
  const byArea = new Map<string, { answered: number; total: number }>();
  for (const q of questions) {
    if (!byArea.has(q.block_key)) {
      byArea.set(q.block_key, { answered: 0, total: 0 });
      order.push(q.block_key);
    }
    const cell = byArea.get(q.block_key)!;
    cell.total += 1;
    if (areaNA.has(q.block_key) || done.has(`${q.block_key}\u0000${q.criterion_key}`)) cell.answered += 1;
  }
  const areas = order.map((k) => {
    const c = byArea.get(k)!;
    return {
      block_key: k,
      answered: c.answered,
      total: c.total,
      complete: c.answered === c.total,
      notApplicable: areaNA.has(k),
    };
  });
  const total = questions.length;
  const answered = areas.reduce((n, a) => n + a.answered, 0);
  return { answered, total, areas, canSubmit: total > 0 && answered === total };
}

/**
 * Where a returning respondent starts (journey slice 5): the first practice
 * area that is not yet complete, so a break never sends them back to area 1.
 * When every area is complete, the last one, which carries "Review and submit".
 */
export function resumeAreaIndex(p: Pick<Progress, "areas">): number {
  const i = p.areas.findIndex((a) => !a.complete);
  return i >= 0 ? i : Math.max(p.areas.length - 1, 0);
}

/**
 * Where to go when an answer has just completed area `current` (journey
 * slice 6): the next unfinished area after it, else the first unfinished one
 * before it, else the last area, which carries "Review and submit".
 */
export function areaAfterCompleting(p: Pick<Progress, "areas">, current: number): number {
  const n = p.areas.length;
  for (let i = current + 1; i < n; i++) if (!p.areas[i]?.complete) return i;
  for (let i = 0; i < current; i++) if (!p.areas[i]?.complete) return i;
  return Math.max(n - 1, 0);
}
